import { useCallback, useEffect, useState } from 'react';
import ReactMarkdown from 'react-markdown';
import { useCopyToClipboard } from '@hooks/useCopyToClipboard';
import {
  Close as CloseIcon,
  ContentCopy as CopyIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from '@mui/material';
import { getAuth0AccessTokenSilently } from '@store/auth0BaseQuery';
import remarkGfm from 'remark-gfm';

const API_BASE_URL = import.meta.env.VITE_API_URL as string;

interface ArtifactContent {
  key: string;
  content_type: string;
  size: number;
  text: string;
  truncated: boolean;
}

interface ArtifactViewerModalProps {
  open: boolean;
  onClose: () => void;
  organizationId: string;
  sessionId: string;
  /** Full S3 key of the artifact, or null when nothing is selected. */
  artifactKey: string | null;
  /** Human-friendly name shown as the title (e.g. `convert.py`). */
  displayName: string;
  /** Open the raw object in a new tab via a presigned URL. */
  onDownload: (key: string) => void;
}

/**
 * Views one converter-provenance artifact inline. Content is fetched from the
 * backend (same-origin — the browser can't read a presigned S3 URL
 * cross-origin), then rendered: Markdown formatted via react-markdown, anything
 * else (convert.py, schemas) as monospace text. The full object is always one
 * click away via Download.
 */
export function ArtifactViewerModal({
  open,
  onClose,
  organizationId,
  sessionId,
  artifactKey,
  displayName,
  onDownload,
}: ArtifactViewerModalProps) {
  const copy = useCopyToClipboard();
  const [content, setContent] = useState<ArtifactContent | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [view, setView] = useState<'rendered' | 'raw'>('rendered');

  const fetchContent = useCallback(async () => {
    if (!artifactKey) return;
    setLoading(true);
    setError(null);
    setContent(null);
    try {
      const token = await getAuth0AccessTokenSilently();
      const params = new URLSearchParams({
        organization_id: organizationId,
        key: artifactKey,
      });
      const res = await fetch(
        `${API_BASE_URL}/v2/admin/data-migration/sessions/${sessionId}/artifacts/content?${params}`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      if (!res.ok) {
        throw new Error(`Failed to load artifact (${res.status})`);
      }
      setContent((await res.json()) as ArtifactContent);
    } catch {
      setError('Could not load this artifact.');
    } finally {
      setLoading(false);
    }
  }, [artifactKey, organizationId, sessionId]);

  useEffect(() => {
    if (open && artifactKey) {
      setView('rendered');
      void fetchContent();
    }
  }, [open, artifactKey, fetchContent]);

  const isMarkdown = content?.content_type === 'text/markdown';
  const showRendered = isMarkdown && view === 'rendered';

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="md">
      <DialogTitle sx={{ pr: 6 }}>
        <Typography
          variant="h6"
          component="span"
          sx={{ fontFamily: 'monospace', fontSize: 16 }}
        >
          {displayName}
        </Typography>
        <IconButton
          aria-label="Close"
          onClick={onClose}
          sx={{ position: 'absolute', right: 8, top: 8 }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
            <CircularProgress size={24} />
          </Box>
        ) : error ? (
          <Alert severity="error">{error}</Alert>
        ) : content ? (
          <>
            {content.truncated && (
              <Alert severity="warning" sx={{ mb: 2 }}>
                Showing a truncated preview ({content.size.toLocaleString()}{' '}
                bytes total). Download the file for the full content.
              </Alert>
            )}
            {isMarkdown && (
              <ToggleButtonGroup
                size="small"
                exclusive
                value={view}
                onChange={(_event, next: 'rendered' | 'raw' | null) => {
                  if (next) setView(next);
                }}
                sx={{ mb: 2 }}
              >
                <ToggleButton value="rendered">Rendered</ToggleButton>
                <ToggleButton value="raw">Raw</ToggleButton>
              </ToggleButtonGroup>
            )}
            {showRendered ? (
              <Box sx={{ '& pre': { overflow: 'auto' }, '& img': { maxWidth: '100%' } }}>
                <ReactMarkdown remarkPlugins={[remarkGfm]}>
                  {content.text}
                </ReactMarkdown>
              </Box>
            ) : (
              <Box
                component="pre"
                sx={{
                  m: 0,
                  p: 1.5,
                  bgcolor: 'grey.50',
                  borderRadius: 1,
                  fontFamily: 'monospace',
                  fontSize: 13,
                  whiteSpace: 'pre-wrap',
                  wordBreak: 'break-word',
                  maxHeight: '60vh',
                  overflow: 'auto',
                }}
              >
                {content.text}
              </Box>
            )}
          </>
        ) : null}
      </DialogContent>
      <DialogActions>
        {content && (
          <Tooltip title="Copy contents">
            <IconButton
              aria-label="Copy artifact contents"
              onClick={() => copy(content.text, displayName)}
            >
              <CopyIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        )}
        {artifactKey && (
          <Button
            startIcon={<DownloadIcon />}
            onClick={() => onDownload(artifactKey)}
          >
            Download
          </Button>
        )}
        <Button onClick={onClose}>Close</Button>
      </DialogActions>
    </Dialog>
  );
}
